import { ReactElement } from 'react'

import { Container, Grid, Theme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { FacebookShareButton, TwitterShareButton } from 'react-share'
import useMediaQuery from '@mui/material/useMediaQuery'

import LinkIcon from 'public/images/link.svg'
import ModalHeader from '@/components/molecules/ModalHeader'
import WishesStatus from '@/components/molecules/WishesStatus'
import FacebookIcon from 'public/images/facebook.svg'
import TwitterIcon from 'public/images/twitter.svg'
import routes from '@/config/routes'
import { WishPlanted } from '@/model/interfaces/wish'

import ComponentStyled from './styled'

type ShareItemProps = {
  name: string
  icon: ReactElement
  onClick?: () => void
}

const ShareItem = ({ name, icon, onClick }: ShareItemProps) => {
  return (
    <div className='share-item' onClick={onClick}>
      {icon}
      <p className='share-item-copy'>{name}</p>
    </div>
  )
}

type Props = {
  wishPlanted: WishPlanted
}

const Share = ({ wishPlanted }: Props): JSX.Element => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  const origin = typeof window !== 'undefined' && window.location.origin ? window.location.origin : ''

  const share = {
    url: `${origin}${routes.wishes}?wish=${wishPlanted?.id}`,
    hashtag: t('#PlantYourWish'),
    title: t(
      '#PlantYourWish to seed a little piece of a better world toghether. For each virtual seed planted, Schär will make a donation to preserve Biodiversity.'
    ),
  }

  const handleClickCopyLink = () => {
    try {
      navigator.clipboard.writeText(share.url)
      toast.success(t('Url copied to clipboard'))
    } catch {
      toast.error(t('An error has occurred'))
    }
  }

  return (
    <ComponentStyled>
      <Container maxWidth='xl'>
        <Grid container spacing={4} justifyContent='flex-end' flexDirection={isMobile ? 'column-reverse' : 'inherit'}>
          <Grid item xs={12} md={6}>
            <div className='share-box'>
              <ModalHeader title={t('share this!')} />
              <div className='share-content'>
                <p className='share-desc'>
                  {t('Help us to reach our goals and conserve all crops selected for the next 100 years.')}
                </p>

                <div className='share-items'>
                  <FacebookShareButton
                    url={share.url}
                    quote={share.title}
                    hashtag={share.hashtag}
                    className='share-container'
                  >
                    <ShareItem name={t('Facebook')} icon={<FacebookIcon />} />
                  </FacebookShareButton>
                  <TwitterShareButton
                    title={share.title}
                    url={share.url}
                    hashtags={[share.hashtag]}
                    className='share-container'
                  >
                    <ShareItem name={t('Twitter')} icon={<TwitterIcon />} />
                  </TwitterShareButton>
                  <ShareItem name={t('Copy link')} onClick={handleClickCopyLink} icon={<LinkIcon />} />
                </div>
              </div>
            </div>
          </Grid>
          <Grid item xs={12} md={3}>
            <WishesStatus color='black' />
          </Grid>
        </Grid>
      </Container>
    </ComponentStyled>
  )
}

export default Share
